/** Attempts: practice, lesson tests and topic checks, and the history of finished ones. */
import { baseApi } from '../baseApi';
import type {
  Answer,
  AnswerOut,
  AttemptKind,
  AttemptOut,
  HistoryPage,
} from '../types';
export interface AttemptRef {
  id: string;
  lang: string;
}

export interface SubmitAnswerArgs {
  attemptId: string;
  itemIndex: number;
  answer: Answer;
  /** Makes a repeated delivery of the same answer harmless. */
  idempotencyKey: string;
  lang: string;
}

export const attemptsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    startAttempt: build.mutation<
      AttemptOut,
      { kind: AttemptKind; targetId: string; restart?: boolean; lang: string }
    >({
      query: ({ lang, kind, targetId, restart = false }) => ({
        url: '/attempts',
        method: 'POST',
        params: { lang },
        body: { kind, targetId, restart },
      }),
      async onQueryStarted({ lang }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(attemptsApi.util.upsertQueryData('getAttempt', { id: data.id, lang }, data));
        } catch {
          // The component reads the error from the mutation state.
        }
      },
    }),
    getAttempt: build.query<AttemptOut, AttemptRef>({
      query: ({ id, lang }) => ({ url: `/attempts/${id}`, params: { lang } }),
      providesTags: (_result, _error, { id }) => [{ type: 'Attempt', id }],
    }),
    submitAnswer: build.mutation<AnswerOut, SubmitAnswerArgs>({
      query: ({ attemptId, itemIndex, answer, idempotencyKey }) => ({
        url: `/attempts/${attemptId}/answers`,
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: { itemIndex, answer },
      }),
      async onQueryStarted({ attemptId, lang }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // The attempt in the cache is the single place the UI reads results from.
          dispatch(
            attemptsApi.util.updateQueryData('getAttempt', { id: attemptId, lang }, (attempt) => {
              const item = attempt.items[data.itemIndex];
              if (item) item.result = data.result;
            }),
          );
        } catch {
          // The outbox decides whether to retry.
        }
      },
    }),
    finishAttempt: build.mutation<AttemptOut, AttemptRef>({
      query: ({ id, lang }) => ({ url: `/attempts/${id}/finish`, method: 'POST', params: { lang } }),
      // A finished test is a new line of the history, and may have gone to a teacher.
      invalidatesTags: ['Progress', 'History', 'Submissions'],
      async onQueryStarted({ lang }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(attemptsApi.util.upsertQueryData('getAttempt', { id: data.id, lang }, data));
        } catch {
          // Reported through the mutation state.
        }
      },
    }),

    // Every finished test and check, the latest first.
    listHistory: build.query<HistoryPage, { lang: string; cursor?: string }>({
      query: (params) => ({ url: '/attempts', params }),
      providesTags: ['History'],
    }),
  }),
});

export const {
  useStartAttemptMutation,
  useGetAttemptQuery,
  useFinishAttemptMutation,
  useListHistoryQuery,
} = attemptsApi;
